import { useEffect, useMemo, useState } from "react";

const MIN_SHEET_HEIGHT_PX = 200;
const MAX_SHEET_VH_RATIO = 0.92;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function normalizeSnapPoint(value: string | number, viewportHeight: number): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.endsWith("%")) {
      const numeric = Number(trimmed.slice(0, -1));
      if (!Number.isFinite(numeric)) return null;
      return (numeric / 100) * viewportHeight;
    }
    const numeric = Number(trimmed);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
}

export function useBottomSheetSnap({
  snapPoints,
  initialSnap = 0,
}: {
  snapPoints?: Array<string | number>;
  initialSnap?: number;
}) {
  const [viewportHeight, setViewportHeight] = useState<number>(() =>
    typeof window !== "undefined" ? window.innerHeight : 900
  );

  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const normalizedSnapHeights = useMemo(() => {
    const points = Array.isArray(snapPoints) ? snapPoints : [];
    if (points.length === 0) return [];
    const maxAllowed = viewportHeight * MAX_SHEET_VH_RATIO;
    const parsed = points
      .map((point) => normalizeSnapPoint(point, viewportHeight))
      .filter((n): n is number => n != null && Number.isFinite(n))
      .map((n) => clamp(n, MIN_SHEET_HEIGHT_PX, maxAllowed))
      .sort((a, b) => a - b);
    return Array.from(new Set(parsed));
  }, [snapPoints, viewportHeight]);

  const hasSnapPoints = normalizedSnapHeights.length > 0;
  const maxSnapHeight = hasSnapPoints
    ? normalizedSnapHeights[normalizedSnapHeights.length - 1]
    : null;
  const clampedInitialSnapIndex = hasSnapPoints
    ? clamp(initialSnap, 0, normalizedSnapHeights.length - 1)
    : 0;
  const initialHeight = hasSnapPoints ? normalizedSnapHeights[clampedInitialSnapIndex] : null;
  const initialTranslateY =
    hasSnapPoints && maxSnapHeight != null && initialHeight != null
      ? maxSnapHeight - initialHeight
      : 0;
  const snapTranslatePoints =
    hasSnapPoints && maxSnapHeight != null
      ? normalizedSnapHeights.map((h) => maxSnapHeight - h).sort((a, b) => a - b)
      : undefined;

  return {
    hasSnapPoints,
    maxSnapHeight,
    initialTranslateY,
    snapTranslatePoints,
  };
}

